/* eslint-disable */
import React from "react";
import { Box, Button, Center, Flex, Heading, Text } from "@chakra-ui/react";
import { apm } from "@elastic/apm-rum";

const RELOAD_GUARD_KEY = "ldh-os-chunk-reload-attempted";

function isChunkLoadError(error) {
  if (!error) return false;
  const name = error.name || "";
  const message = error.message || "";
  return (
    name === "ChunkLoadError" ||
    /loading chunk [\w.-]+ failed/i.test(message) ||
    /loading css chunk [\w.-]+ failed/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message)
  );
}

function readReloadGuard() {
  try {
    return sessionStorage.getItem(RELOAD_GUARD_KEY) === "1";
  } catch {
    return false;
  }
}

function writeReloadGuard() {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
  } catch {
    // manual fallback UI instead of looping.
  }
}

function clearReloadGuard() {
  try {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    // ignore
  }
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false, alreadyAttemptedReload: false };
  }

  static getDerivedStateFromError(error) {
    const isChunkError = isChunkLoadError(error);
    return {
      hasError: true,
      isChunkError,
      alreadyAttemptedReload: isChunkError && readReloadGuard(),
    };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught an error:", error, info);
    try {
      apm.captureError(error);
    } catch {
      // APM not available/initialized - non-fatal
    }

    if (this.state.isChunkError && !this.state.alreadyAttemptedReload) {
      writeReloadGuard();
      window.location.reload();
    }
  }

  componentDidMount() {
    clearReloadGuard();
  }

  handleManualReload = () => {
    clearReloadGuard();
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.state.isChunkError && !this.state.alreadyAttemptedReload) {
      return (
        <Center minH="40vh">
          <Text color="var(--color-text-secondary)" fontSize="sm">
            Neue Version wird geladen …
          </Text>
        </Center>
      );
    }

    return (
      <Center minH="60vh" px={4}>
        <Box
          maxW="440px"
          w="100%"
          bg="var(--color-bg-card)"
          border="1px solid"
          borderColor="var(--color-border-medium)"
          borderRadius="10px"
          p="32px"
          textAlign="center"
        >
          <Flex direction="column" align="center">
            <Heading as="h2" size="md" color="var(--color-text-primary)" mb={2}>
              Etwas ist schiefgelaufen
            </Heading>
            <Text color="var(--color-text-secondary)" fontSize="sm" mb={6}>
              {this.state.isChunkError
                ? "Es gab ein Problem beim Laden einer aktualisierten Version der Anwendung."
                : "Es ist ein unerwarteter Fehler aufgetreten."}{" "}
              Bitte laden Sie die Seite neu.
            </Text>
            <Button
              onClick={this.handleManualReload}
              bg="var(--color-brand-primary)"
              color="white"
              _hover={{ opacity: 0.9 }}
            >
              Seite neu laden
            </Button>
          </Flex>
        </Box>
      </Center>
    );
  }
}

export default ErrorBoundary;
